import * as React from 'react';
import { useState, useEffect } from 'react';
import { ApiHelper } from '../ApiHelper';
import { DetailsList, DetailsListLayoutMode, IColumn } from '@fluentui/react/lib/DetailsList';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { TextField } from '@fluentui/react/lib/TextField';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { useBoolean } from '@fluentui/react-hooks';
import { Separator } from '@fluentui/react/lib/Separator';
import '../styles/common.css';

interface Setting {
    name: string;
    value: string | null;
    lastUpdated: string;
    updatedBy: string | null;
}

interface CreateSettingRequest {
    name: string;
    value: string;
}

const SETTING_VALUE_PREVIEW_LENGTH = 180;
const SETTING_VALUE_METADATA_LINE_THRESHOLD = 4;

function getSettingValuePreview(value: string | null | undefined) {
    if (!value) {
        return '';
    }

    const collapsed = value.replace(/\s+/g, ' ').trim();
    if (collapsed.length <= SETTING_VALUE_PREVIEW_LENGTH) {
        return collapsed;
    }

    return `${collapsed.slice(0, SETTING_VALUE_PREVIEW_LENGTH - 3)}...`;
}

function getSettingValueMetadata(value: string | null | undefined) {
    if (!value) {
        return null;
    }

    const lineCount = value.split(/\r?\n/).length;
    const showCharCount = value.length > SETTING_VALUE_PREVIEW_LENGTH;
    const showLineCount = lineCount > SETTING_VALUE_METADATA_LINE_THRESHOLD;

    if (!showCharCount && !showLineCount) {
        return null;
    }

    const parts: string[] = [];
    if (showCharCount) {
        parts.push(`${value.length.toLocaleString()} chars`);
    }
    if (showLineCount) {
        parts.push(`${lineCount.toLocaleString()} line${lineCount === 1 ? '' : 's'}`);
    }

    return parts.join(' • ');
}

export function SettingsView() {
    const [settings, setSettings] = useState<Setting[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // Panel for create/edit form
    const [isPanelOpen, { setTrue: openPanel, setFalse: closePanel }] = useBoolean(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    // Form state
    const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
    const [formData, setFormData] = useState<CreateSettingRequest>({
        name: '',
        value: ''
    });

    const fetchSettings = async () => {
        try {
            setError(null);
            setLoading(true);
            
            const response = await ApiHelper<Setting[]>('/api/settings', 'GET');
            setSettings(response);
        } catch (err) {
            console.error('Error fetching settings:', err);
            setError('An error occurred while loading settings');
            setSettings([]);
        } finally {
            setLoading(false);
        }
    };

    const createOrUpdateSetting = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            
            // Validate form
            if (!formData.name || !formData.value) {
                setSubmitError('Name and value are required');
                return;
            }
            
            const requestBody = {
                name: formData.name.trim(),
                value: formData.value
            };
            
            const response = await ApiHelper<Setting>('/api/settings', 'POST', requestBody);
            
            if (response && response.name) {
                // Success - close panel and refresh
                closePanel();
                resetForm();
                fetchSettings();
            } else {
                setSubmitError('Failed to save setting');
            }
        } catch (err) {
            console.error('Error saving setting:', err);
            setSubmitError('An error occurred while saving the setting');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            value: ''
        });
        setEditingSetting(null);
        setSubmitError(null);
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleEdit = (item: Setting) => {
        setEditingSetting(item);
        setFormData({
            name: item.name,
            value: item.value || ''
        });
        openPanel();
    };

    const handleAddNew = () => {
        setEditingSetting(null);
        setFormData({
            name: '',
            value: ''
        });
        openPanel();
    };

    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'create',
            text: 'Create New Setting',
            iconProps: { iconName: 'Add' },
            onClick: handleAddNew
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => fetchSettings()
        }
    ];

    const columns: IColumn[] = [
        {
            key: 'name',
            name: 'Setting Name',
            fieldName: 'name',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            onRender: (item: Setting) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        handleEdit(item);
                    }}
                    style={{ fontWeight: 600 }}
                >
                    {item.name}
                </a>
            )
        },
        {
            key: 'value',
            name: 'Value',
            fieldName: 'value',
            minWidth: 220,
            maxWidth: 420,
            isResizable: true,
            onRender: (item: Setting) => {
                const metadata = getSettingValueMetadata(item.value);

                return (
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text
                            style={{
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '12px'
                            }}
                        >
                            {item.value ? getSettingValuePreview(item.value) : <em style={{ color: '#666' }}>No value</em>}
                        </Text>
                        {metadata && (
                            <Text variant="small" style={{ color: '#605e5c' }}>
                                {metadata}
                            </Text>
                        )}
                    </Stack>
                );
            }
        },
        {
            key: 'lastUpdated',
            name: 'Last Updated',
            fieldName: 'lastUpdated',
            minWidth: 130,
            maxWidth: 160,
            isResizable: true,
            onRender: (item: Setting) =>
                item.lastUpdated ? new Date(item.lastUpdated).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                }) : 'Unknown'
        },
        {
            key: 'updatedBy',
            name: 'Updated By',
            fieldName: 'updatedBy',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: Setting) => item.updatedBy || 'System'
        }
    ];

    return (
        <div style={{ margin: '20px' }}>
            <Stack tokens={{ childrenGap: 20 }}>
                <CommandBar items={commandItems} />
                
                <Text variant="xxLarge" as="h1">
                    Application Settings
                </Text>

                <Text variant="medium" style={{ color: '#605e5c' }}>
                    {loading ? 'Loading...' : `${settings.length} total settings`}
                </Text>

                {error && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {error}
                    </MessageBar>
                )}

                {loading ? (
                    <Spinner size={SpinnerSize.large} label="Loading settings..." />
                ) : (
                    <DetailsList
                        items={settings}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={0}
                        compact={true}
                    />
                )}

                {/* Empty state */}
                {!loading && settings.length === 0 && (
                    <Text variant="medium" style={{ color: '#605e5c' }}>
                        No settings found. Click "Create New Setting" to add your first setting.
                    </Text>
                )}
            </Stack>

            {/* Create/Edit Setting Panel */}
            <Panel
                isOpen={isPanelOpen}
                onDismiss={closePanel}
                type={PanelType.large}
                headerText={editingSetting ? "Edit Setting" : "Create New Setting"}
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton
                            text={editingSetting ? "Update Setting" : "Create Setting"}
                            onClick={createOrUpdateSetting}
                            disabled={isSubmitting}
                        />
                        <DefaultButton
                            text="Cancel"
                            onClick={() => {
                                closePanel();
                                resetForm();
                            }}
                            disabled={isSubmitting}
                        />
                    </Stack>
                )}
            >
                <Stack tokens={{ childrenGap: 15 }}>
                    {submitError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {submitError}
                        </MessageBar>
                    )}

                    {isSubmitting && (
                        <Spinner size={SpinnerSize.medium} label="Saving setting..." />
                    )}

                    <TextField
                        label="Setting Name"
                        value={formData.name}
                        onChange={(ev, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                name: newValue || ''
                            }));
                        }}
                        placeholder="e.g., MaxRequestTimeout"
                        required
                        disabled={editingSetting !== null || isSubmitting}
                        description={editingSetting ? "Setting name cannot be changed" : undefined}
                    />

                    <TextField
                        label="Setting Value"
                        value={formData.value}
                        onChange={(ev, newValue) => {
                            setFormData(prev => ({
                                ...prev,
                                value: newValue || ''
                            }));
                        }}
                        placeholder="Enter the setting value"
                        multiline
                        rows={18}
                        disabled={isSubmitting}
                        description={getSettingValueMetadata(formData.value) ?? undefined}
                        styles={{
                            fieldGroup: {
                                minHeight: 360,
                            },
                            field: {
                                fontFamily: 'Consolas, "Courier New", monospace',
                                whiteSpace: 'pre',
                            },
                        }}
                    />

                    <Separator />

                    <Text variant="small" style={{ color: '#605e5c' }}>
                        * Required fields
                    </Text>
                </Stack>
            </Panel>
        </div>
    );
}